import { UntitledIcon, type UntitledIconData } from '@faceup/icons'
import { ulAlertCircle } from '@faceup/icons/ulAlertCircle'
import { ulCheckCircle } from '@faceup/icons/ulCheckCircle'
import { ulInfoCircle } from '@faceup/icons/ulInfoCircle'
import { ulXCircle } from '@faceup/icons/ulXCircle'
import { ulXClose } from '@faceup/icons/ulXClose'
import { App, type NotificationArgsProps } from 'antd'
import { type Key, useMemo } from 'react'

export const notificationTypes = ['basic', 'error', 'info', 'success', 'warning'] as const
export type NotificationType = (typeof notificationTypes)[number]

export type NotificationArgs = Omit<NotificationArgsProps, 'type' | 'icon' | 'closeIcon'>

const icons: Record<NotificationType, { icon: UntitledIconData; color: string } | null> = {
  basic: null,
  info: { icon: ulInfoCircle, color: 'var(--Colors-Brand-Primary-colorPrimary)' },
  error: { icon: ulXCircle, color: 'var(--Colors-Brand-Error-colorError)' },
  success: { icon: ulCheckCircle, color: 'var(--Colors-Brand-Success-colorSuccess)' },
  warning: { icon: ulAlertCircle, color: 'var(--Colors-Brand-Warning-colorWarning)' },
}

const getIcon = (type: NotificationType) => {
  const icon = icons[type]

  return icon ? <UntitledIcon icon={icon.icon} color={icon.color} size={24} /> : undefined
}

export const useNotification = () => {
  const app = App.useApp()

  return useMemo(() => {
    const createNotification = (
      type: NotificationType,
      { actions, message, title, ...props }: NotificationArgsProps
    ) => {
      const mergedTitle = title ?? message
      return app.notification.open({
        ...props,
        icon: getIcon(type),
        title: actions ? <strong>{mergedTitle}</strong> : mergedTitle,
        actions,
        closeIcon: (
          <UntitledIcon icon={ulXClose} size={22} color='var(--Colors-Neutral-Text-colorText)' />
        ),
      })
    }

    const open = Object.fromEntries(
      notificationTypes.map(type => [
        type,
        (notificationArgs: NotificationArgs) => createNotification(type, notificationArgs),
      ])
    ) as Record<NotificationType, (args: NotificationArgs) => void>

    return {
      ...open,
      destroy: (key?: Key) => app.notification.destroy(key),
    }
  }, [app])
}
